import React, { useState } from 'react';
import {
  Typography, Box, Paper, Grid, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer
} from '@mui/material';
import DateRangeFilter, { defaultStart, defaultEnd } from './DateRangeFilter';
import { ReportStatistics } from '../../../api';

const StatCard = ({ label, value }) => (
  <Paper sx={{ p: 2, textAlign: 'center' }}>
    <Typography variant="h4" fontWeight={300}>{value ?? '—'}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
);

const SOURCE_LABELS = { manual: 'Manual (Staff)', mobile_qr: 'Mobile QR', kiosk: 'Kiosk' };

const StatisticsReport = ({ access }) => {
  const [start, setStart] = useState(defaultStart());
  const [end, setEnd] = useState(defaultEnd());
  const [data, setData] = useState(null);

  const run = () =>
    ReportStatistics(access?.org?.id, { start, end }).then(setData).catch(() => {});

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>Statistics</Typography>
      <DateRangeFilter start={start} end={end} onStart={setStart} onEnd={setEnd} onSubmit={run} />
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatCard label="Total Visits" value={data.total} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Completed" value={data.completed} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Cancelled / No Show" value={data.cancelled} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Avg Wait (min)" value={data.avgWaitMinutes} /></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Visits by Source</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Source</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(data.bySource).map(([src, count]) => (
                      <TableRow key={src}>
                        <TableCell>{SOURCE_LABELS[src] || src}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                    {!Object.keys(data.bySource).length && (
                      <TableRow><TableCell colSpan={2} align="center">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Visits by Day</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 240, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Date</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(data.byDay).sort(([a], [b]) => a.localeCompare(b)).map(([day, count]) => (
                      <TableRow key={day}>
                        <TableCell>{day}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                    {!Object.keys(data.byDay).length && (
                      <TableRow><TableCell colSpan={2} align="center">No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Visits by Hour of Day</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead><TableRow>
                  {data.byHour.map((_, h) => (
                    <TableCell key={h} align="center" sx={{ px: 0.5, minWidth: 32 }}>
                      {h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}
                    </TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody><TableRow>
                  {data.byHour.map((count, h) => (
                    <TableCell key={h} align="center" sx={{
                      px: 0.5,
                      bgcolor: count > 0 ? `rgba(25,118,210,${Math.min(count / Math.max(...data.byHour, 1), 1) * 0.7 + 0.1})` : 'transparent',
                      fontWeight: count > 0 ? 600 : 400,
                      color: count > 0 ? '#fff' : 'text.disabled',
                    }}>
                      {count || ''}
                    </TableCell>
                  ))}
                </TableRow></TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </>
  );
};

export default StatisticsReport;
